import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useConnectFireflies } from "../../api/generated/generated";
import { getErrorMessage } from "../../api/client";

interface ConnectFirefliesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ConnectFirefliesModal({
  open,
  onClose,
  onSuccess,
}: ConnectFirefliesModalProps) {
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const connectMutation = useConnectFireflies();

  const handleSubmit = async () => {
    if (apiKey.length < 10) {
      setError("API key must be at least 10 characters.");
      return;
    }

    setError(null);
    try {
      await connectMutation.mutateAsync({ data: { api_key: apiKey } });
      setApiKey("");
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleClose = () => {
    setApiKey("");
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Connect Fireflies</DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 2
          }}>
          Enter your Fireflies API key to connect your account. You can find
          your API key in your{" "}
          <Link
            href="https://app.fireflies.ai/integrations/custom/fireflies"
            target="_blank"
            rel="noopener noreferrer"
          >
            Fireflies API settings
          </Link>
          .
        </Typography>

        <TextField
          autoFocus
          label="API Key"
          type="password"
          fullWidth
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Enter your Fireflies API key"
          sx={{ mt: 1 }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && apiKey.length >= 10) {
              handleSubmit();
            }
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={apiKey.length < 10 || connectMutation.isPending}
        >
          {connectMutation.isPending ? "Connecting..." : "Connect"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
